/**
 * DenSwitcherModal — the modal that lets a user browse the 27-persona
 * catalog, click into a preview, and commit to switch.
 *
 * Mounted as a portal-style overlay. Triggered from a "Switch Den"
 * button visible in any Den header (DenSwitchButton.tsx).
 *
 * Preview mode: selecting a persona navigates to /dashboard/dens/{persona-key}?preview=1
 * which renders the target Den against sample_client_id from the catalog.
 *
 * Commit: from preview mode, "Switch to this Den" calls the
 * commit-den-switch edge function (or direct upsert) to update
 * user_active_dens, then navigates to /dashboard/dens/{persona-key}.
 *
 * Add-second-Den mode: triggered when Pro+ user clicks "Add a second Den"
 * — same modal, different commit (insert non-primary, don't replace primary).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Lock, MessageSquarePlus } from "lucide-react";
import { DenPersonaRequestModal } from "./DenPersonaRequestModal";
import { isDenBuilt, compareDensByBuildStatus } from "@/lib/dens";

interface CatalogEntry {
  persona_key: string;
  display_name: string;
  short_description: string;
  best_for_tags: string[] | null;
  hero_score_label: string | null;
  pricing_fit: string | null;
  status: "built" | "specced_unbuilt" | "planned";
  marketing_blurb: string | null;
  morality_commitment: string | null;
  icon_name: string | null;
  display_order: number | null;
}

interface Props {
  open: boolean;
  mode?: "switch" | "add";
  onClose: () => void;
}

export function DenSwitcherModal({ open, mode = "switch", onClose }: Props) {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "built" | "coming_soon">("all");
  const [requestOpen, setRequestOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("den_catalog" as any)
        .select("persona_key, display_name, short_description, best_for_tags, hero_score_label, pricing_fit, status, marketing_blurb, morality_commitment, icon_name, display_order")
        .order("display_order", { ascending: true });
      if (!alive) return;
      setCatalog((data as unknown as CatalogEntry[]) ?? []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;

  // Source of truth is src/lib/dens.ts (BUILT_DENS). The den_catalog.status
  // column is unreliable — every row says 'built' regardless of ship state.
  const builtCount = catalog.filter((c) => isDenBuilt(c.persona_key)).length;
  const comingSoonCount = catalog.length - builtCount;

  const visible = catalog
    .filter((c) => {
      const built = isDenBuilt(c.persona_key);
      if (filter === "built") return built;
      if (filter === "coming_soon") return !built;
      return true;
    })
    .slice()
    .sort(compareDensByBuildStatus);

  function preview(personaKey: string) {
    if (!isDenBuilt(personaKey)) return;
    onClose();
    navigate(`/dashboard/dens/${personaKey}?preview=1${mode === "add" ? "&add=1" : ""}`);
  }

  function notifyMe(personaKey: string) {
    // Non-blocking: log the interest, show a toast. Coming-soon dens are
    // not clickable from the card click handler now (cards are visually
    // greyed and the onClick path is a no-op for !built). This path is
    // here in case a future surface still wires a click for interest.
    // REMOVED PER CLAUDE.md: non-built dens are already greyed out with
    // "write us if needed", so this path is unreachable from the UI today, and
    // den_catalog_interest does not exist in the database anyway. Registering
    // interest was never recorded. Nothing is written rather than pretending —
    // if this becomes a real feature it needs a table first.
    void personaKey;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-foxchat-id="den-switcher-modal"
      >
        <div
          className="bg-background rounded-2xl border border-border max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                {mode === "add" ? "Add a second Den" : "Switch your Den"}
              </h2>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                27 daily home pages. Pick the one that matches the work you actually do.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full hover:bg-muted/30 p-1.5"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 p-3 border-b border-border/40 bg-muted/5">
            <FilterPill label={`All (${catalog.length})`} active={filter === "all"} onClick={() => setFilter("all")} />
            <FilterPill label={`Built (${builtCount})`} active={filter === "built"} onClick={() => setFilter("built")} />
            <FilterPill label={`Coming soon (${comingSoonCount})`} active={filter === "coming_soon"} onClick={() => setFilter("coming_soon")} />
          </div>

          {/* Grid */}
          <div className="overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loading ? (
              Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="h-32 rounded-xl border border-border/40 bg-muted/10 animate-pulse" />
              ))
            ) : (
              visible.map((c) => {
                const built = isDenBuilt(c.persona_key);
                return (
                  <DenCard
                    key={c.persona_key}
                    entry={c}
                    built={built}
                    onClick={() => built ? preview(c.persona_key) : notifyMe(c.persona_key)}
                  />
                );
              })
            )}
            {!loading && visible.length === 0 && (
              <div className="col-span-full text-center text-[13px] text-muted-foreground py-8">
                No personas in this filter.
              </div>
            )}
          </div>

          <div className="p-3 border-t border-border/40 bg-muted/5 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-muted-foreground">
              Click any built Den to preview with sample data. Confirm in preview to switch.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] gap-1.5"
              onClick={() => setRequestOpen(true)}
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              My career isn't here
            </Button>
          </div>
        </div>
        <DenPersonaRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
      </div>
    </TooltipProvider>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
        active ? "bg-blue-500/15 text-blue-600 border-blue-500/40" : "bg-background border-border/40 text-muted-foreground hover:bg-muted/20"
      }`}
    >
      {label}
    </button>
  );
}

function DenCard({ entry, built, onClick }: { entry: CatalogEntry; built: boolean; onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={built ? onClick : undefined}
          aria-disabled={!built}
          tabIndex={built ? 0 : -1}
          className={`text-left rounded-xl border bg-background p-3 transition-colors ${
            built
              ? "border-border/60 hover:border-blue-500/40 hover:bg-blue-500/5 cursor-pointer"
              : "border-border/30 bg-muted/10 opacity-50 cursor-not-allowed pointer-events-none"
          }`}
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <span className="text-[13px] font-bold leading-tight">{entry.display_name}</span>
            {!built && (
              <span className="text-[9px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded bg-muted/30 text-muted-foreground flex items-center gap-1">
                <Lock className="h-2.5 w-2.5" /> Coming soon
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug mb-1.5 line-clamp-2">
            {built ? entry.short_description : "Custom-built on request"}
          </p>
          {built && entry.best_for_tags && entry.best_for_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {entry.best_for_tags.slice(0, 2).map((t) => (
                <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted/30 text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          )}
          {built && entry.hero_score_label && (
            <p className="text-[10px] text-muted-foreground italic mt-1">{entry.hero_score_label}</p>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">
        {built
          ? (entry.morality_commitment || entry.marketing_blurb || entry.short_description)
          : "Coming soon — write us if you need this den activated"}
      </TooltipContent>
    </Tooltip>
  );
}

export default DenSwitcherModal;
